/* eslint-disable react-hooks/exhaustive-deps */

import { useTranslation } from 'react-i18next';

import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import KeyboardArrowDownOutlined from '@mui/icons-material/KeyboardArrowDownOutlined';

import { useResponsive } from 'src/hooks/use-responsive';
import { useModalBackHandler } from 'src/hooks/use-modal-back-handler';
import { PolicyType, useTermsPolicies } from 'src/hooks/use-terms-policies';

import Iconify from '../iconify';
import TypographyHTML from '../typography-html';

export interface Props {
  open: boolean;
  onClose: VoidFunction;
  type: PolicyType;
}

export default function JoinPolicyModal({ open, onClose, type }: Readonly<Props>) {
  const { t } = useTranslation();

  const isMobile = useResponsive('down', 'md');

  useModalBackHandler(open, onClose);

  const { effectiveDate, htmlContent } = useTermsPolicies({ type });

  const renderEffectiveDate = (
    <Stack direction="row" spacing={1} py={1} alignItems="center">
      <Typography variant="subtitle1">{t('learner.3715')}</Typography>
      <Button
        variant="outlined"
        size="small"
        color="inherit"
        sx={{ color: 'var(--text-secondary)' }}
        endIcon={<KeyboardArrowDownOutlined />}
      >
        {effectiveDate}
      </Button>
    </Stack>
  );

  const renderTerms = htmlContent && (
    <Stack>
      <TypographyHTML content={htmlContent} />
    </Stack>
  );

  const renderCloseIcon = (isShowing: boolean) => (
    <IconButton sx={{ opacity: !isShowing ? 0 : 1 }} disabled={!isShowing} onClick={onClose}>
      <Iconify
        color="var(--text-primary)"
        width={{ xs: 24, md: 32 }}
        height={{ xs: 24, md: 32 }}
        icon="mdi:close"
      />
    </IconButton>
  );

  return (
    <>
      {htmlContent && (
        <Dialog open={open} fullScreen={isMobile} onClose={onClose} maxWidth="sm">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            minHeight={{ xs: 64, md: 'unset' }}
            height="fit-content"
            spacing={0.5}
            p={{ xs: '0 12px', md: 3 }}
          >
            {isMobile && renderCloseIcon(isMobile)}

            <DialogTitle
              sx={{
                p: 0,
                flexGrow: { xs: 1, md: 0 },
                textAlign: { xs: 'center', md: 'left' },
                typography: { xs: 'h4', md: 'h3' },
                height: { md: 48 },
              }}
            >
              {t(type === PolicyType.TERMS ? 'learner.1605' : 'learner.1224')}
            </DialogTitle>

            {renderCloseIcon(!isMobile)}
          </Stack>

          <DialogContent sx={{ p: 0 }}>
            <Stack p={{ xs: 2, md: '0 16px 16px' }}>
              {renderEffectiveDate}
              {renderTerms}
            </Stack>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
